import type { IPostCandidatesSearch } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { Stack } from "@mui/material";
import { sortIdentity } from "@root/utils/sortIdentity";
import { SuspenseHandler } from "@styleguide";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { AllCandidatesGrid } from "./allCandidatesGrid";
import { SelectedCandidatesGrid } from "./selectedCandidatesGrid";

export interface ICandidateSelectionTabsProps {
  currentTab: "allCandidates" | "selectedCandidates";
  selectedCandidates: IPostCandidatesSearch["data"];
  localSelectedCandidates: IPostCandidatesSearch["data"];
  disabledCandidates?: { _id: ObjectId; tooltip?: string }[];
  hasFilters: boolean;
  setTotal: Dispatch<SetStateAction<number>>;
  setSelectedCandidates: Dispatch<SetStateAction<IPostCandidatesSearch["data"]>>;
  setLocalSelectedCandidates: Dispatch<SetStateAction<IPostCandidatesSearch["data"]>>;
  findCandidateWorkshop: () => IPostCandidatesSearch;
  scrollerRef: MutableRefObject<HTMLDivElement | null>;
}

export const CandidateSelectionTabs = ({
  currentTab,
  selectedCandidates,
  localSelectedCandidates,
  disabledCandidates,
  hasFilters,
  setTotal,
  setSelectedCandidates,
  setLocalSelectedCandidates,
  findCandidateWorkshop,
  scrollerRef,
}: ICandidateSelectionTabsProps) => {
  const setSortedSelectedCandidates = (candidates: IPostCandidatesSearch["data"]) => {
    setSelectedCandidates(sortIdentity(candidates));
  };

  const selectCandidates = (candidates: IPostCandidatesSearch["data"]) => {
    setSortedSelectedCandidates(candidates);
    setLocalSelectedCandidates(candidates);
  };

  return (
    <Stack sx={() => ({ flex: 1, position: "relative", left: "-9px" })}>
      {currentTab === "allCandidates" && (
        <SuspenseHandler slotProps={{ box: { flex: "auto" } }}>
          <AllCandidatesGrid
            selectedCandidates={selectedCandidates}
            disabledCandidates={disabledCandidates}
            hasFilters={hasFilters}
            setSelectedCandidates={selectCandidates}
            findCandidateWorkshop={findCandidateWorkshop}
            setTotal={setTotal}
            scrollerRef={scrollerRef}
          />
        </SuspenseHandler>
      )}
      {currentTab === "selectedCandidates" && (
        <SelectedCandidatesGrid
          selectedCandidates={selectedCandidates}
          localSelectedCandidates={localSelectedCandidates}
          setLocalSelectedCandidates={setLocalSelectedCandidates}
          scrollerRef={scrollerRef}
        />
      )}
    </Stack>
  );
};
